import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle, Star, Zap, Target, BookOpen, MessageCircle, FileText, Crown, ArrowLeft, Loader2, Map, Briefcase } from "lucide-react";
import axios from "axios";
import { trackInitiateCheckout, getMetaAttributionPayload } from "@/utils/metaPixel";
import { useMarketConfig, price as fmtPrice } from "@/utils/marketConfig";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESUME_ANALYSE_FEATURES = [
  { icon: CheckCircle, title: "ATS compatibility score" },
  { icon: Target, title: "Keyword gap report for your target role" },
  { icon: Zap, title: "AI-powered fix suggestions" },
  { icon: FileText, title: "Download optimised, ATS-ready resume" },
];

const SEASON_PASS_FEATURES = [
  { icon: Star, title: "Everything in Resume Analysis" },
  { icon: Map, title: "AI career roadmap personalised to you" },
  { icon: BookOpen, title: "Skill gap analysis + curated courses" },
  { icon: Briefcase, title: "Smart job matching engine" },
  { icon: Crown, title: "Priority updates as we ship new AI features" },
];

export default function SeasonPassPage() {
  const [loading, setLoading] = useState(false);
  // Market config drives the displayed currency + prices — via the shared hook.
  // Never render a currency until `ready` (a cached/fallback market can be the
  // wrong country and flashes ₹ before $).
  const { cfg: mkt, ready: mktReady } = useMarketConfig();
  const p = (amt) => (mktReady ? fmtPrice(mkt, amt) : "…");
  const navigate = useNavigate();
  const { getHeaders, user } = useAuth();

  const handlePurchase = async () => {
    if (!user) {
      toast.error("Please sign in first");
      navigate("/auth?return=/season-pass");
      return;
    }
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/payment/create-season-checkout`, {
        origin_url: originUrl,
        ...getMetaAttributionPayload(),
      }, { headers: { ...getHeaders(), "Content-Type": "application/json" } });
      trackInitiateCheckout(mkt.prices.pass_annual, mkt.currency, "Full Career OS");
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setLoading(false);
    }
  };

  const handleAnalysis = () => {
    navigate("/intake");
  };

  return (
    <div className="ap-root min-h-screen pt-24 pb-24" data-testid="season-pass-page">
      <div className="ap-section max-w-4xl">
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="ap-chip hover:border-[var(--ap-ink)] transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>

        {/* Hero */}
        <div className="ap-mesh ap-grid-overlay relative text-center mb-14 pt-4">
          <span className="ap-chip ap-rise ap-rise-1" style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet-soft)", background: "var(--ap-violet-soft)" }}>
            <Crown className="w-4 h-4" /> Full Access
          </span>
          <h1 className="ap-display ap-rise ap-rise-2 mt-5 font-extrabold tracking-tight text-[clamp(30px,5vw,52px)] leading-[1.06] text-[var(--ap-ink)]">
            {/* Headline must match the visitor's actual state — only claim they
                "have the resume" once they've actually analysed one. */}
            {user?.has_analysis ? (
              <>You Have the Resume.{" "}<span className="ap-grad-text">Now Get the Roadmap.</span></>
            ) : (
              <>Get Noticed.{" "}<span className="ap-grad-text">Get Hired.</span></>
            )}
          </h1>
          <p className="ap-rise ap-rise-3 text-[var(--ap-muted)] text-base sm:text-lg max-w-xl mx-auto mt-5 leading-relaxed">
            The resume gets you noticed. The Full Career OS gets you hired.
            Smart job matching, skill upskilling, and AI career coaching — all in one.
          </p>
        </div>

        {/* Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16 items-start">
          {/* Resume Analysis (current) */}
          <Card className="ap-card ap-pop relative border-0 p-2">
            <CardHeader className="pt-6">
              <CardTitle className="ap-display text-xl font-bold text-[var(--ap-ink)]">Resume Analysis</CardTitle>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="ap-display text-4xl font-extrabold text-[var(--ap-ink)]" data-testid="analysis-price">{p(mkt.prices.one_time)}</span>
                {p(mkt.prices.one_time_was) && (
                  <span className="text-[var(--ap-muted)] text-sm line-through">{p(mkt.prices.one_time_was)}</span>
                )}
                <span className="text-[var(--ap-muted)] text-sm">one-time</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3.5 mt-1">
                {RESUME_ANALYSE_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[15px]">
                    <CheckCircle className="w-[18px] h-[18px] text-[var(--ap-green)] shrink-0 mt-0.5" />
                    <span className="text-[var(--ap-ink-2)]">{f.title}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={handleAnalysis}
                className="ap-btn ap-btn-ghost ap-btn-lg w-full mt-7"
                data-testid="get-roast-btn"
              >
                GET RESUME ANALYSIS
              </button>
            </CardContent>
          </Card>

          {/* Season Pass */}
          <Card
            className="ap-card ap-pop relative overflow-hidden p-2"
            style={{ borderColor: "var(--ap-violet)", borderWidth: 2 }}
            data-testid="season-pass-card"
          >
            <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: "var(--ap-fire)" }} />
            <CardHeader className="pt-7">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="ap-display text-xl font-bold text-[var(--ap-ink)]">Full Career OS</CardTitle>
                <Badge
                  className="border-0 text-[11px] font-bold tracking-wide uppercase rounded-full px-3 py-1"
                  style={{ background: "var(--ap-violet-soft)", color: "var(--ap-violet)" }}
                >
                  Most popular
                </Badge>
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="ap-display text-4xl font-extrabold text-[var(--ap-ink)]" data-testid="pass-price">{p(mkt.prices.pass_annual)}</span>
                {p(mkt.prices.pass_annual_was) && (
                  <span className="text-[var(--ap-muted)] text-sm line-through">{p(mkt.prices.pass_annual_was)}</span>
                )}
                <span className="text-[var(--ap-muted)] text-sm">/ 1 year</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3.5 mb-7 mt-1">
                {SEASON_PASS_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[15px]">
                    <CheckCircle className="w-[18px] h-[18px] text-[var(--ap-green)] shrink-0 mt-0.5" />
                    <span className="text-[var(--ap-ink-2)]">{f.title}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={handlePurchase}
                disabled={loading}
                className="ap-btn ap-btn-fire ap-btn-lg w-full uppercase tracking-wider disabled:opacity-50"
                data-testid="buy-season-pass-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "GET FULL ACCESS"}
              </button>
            </CardContent>
          </Card>
        </div>

        {/* Feature details */}
        <div className="text-center mb-8">
          <span className="ap-eyebrow">What you unlock</span>
          <h2 className="ap-h2 mt-3" style={{ fontSize: "clamp(24px,3.4vw,38px)" }}>
            Everything you need to <span className="ap-grad-text">get hired</span>.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SEASON_PASS_FEATURES.filter((f, i) => i > 0).map((f, i) => (
            <Card key={i} className="ap-card ap-pop border-0" data-testid={`feature-detail-${i}`}>
              <CardContent className="pt-6 pb-6">
                <div
                  className="w-11 h-11 rounded-xl grid place-items-center mb-4"
                  style={{ background: "var(--ap-violet-soft)" }}
                >
                  <f.icon className="w-5 h-5 text-[var(--ap-violet)]" />
                </div>
                <h3 className="ap-display text-[15px] font-bold text-[var(--ap-ink)]">{f.title}</h3>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Reassurance strip */}
        <div className="ap-card mt-10 flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-3 py-6 px-6 text-center">
          <span className="flex items-center gap-2 text-sm font-medium text-[var(--ap-ink-2)]">
            <CheckCircle className="w-[18px] h-[18px] text-[var(--ap-green)]" /> Secure checkout
          </span>
          <span className="flex items-center gap-2 text-sm font-medium text-[var(--ap-ink-2)]">
            <CheckCircle className="w-[18px] h-[18px] text-[var(--ap-green)]" /> Full year of access
          </span>
          <span className="flex items-center gap-2 text-sm font-medium text-[var(--ap-ink-2)]">
            <CheckCircle className="w-[18px] h-[18px] text-[var(--ap-green)]" /> Cancel anytime
          </span>
        </div>
      </div>
    </div>
  );
}
